import type { AuthHookContext, AuthUserLike, GencowAuthConfig } from "@gencow/core";

function toBetterAuthFieldType(type: string): "string" | "boolean" | "number" | "date" {
  if (type === "boolean") return "boolean";
  if (type === "integer" || type === "number") return "number";
  if (type === "timestamp") return "date";
  return "string";
}

function buildUserAdditionalFields(authConfig?: GencowAuthConfig) {
  const fields = (authConfig as any)?.user?.additionalFields as Record<string, any> | undefined;
  if (!fields || Object.keys(fields).length === 0) return undefined;

  return Object.fromEntries(
    Object.entries(fields).map(([name, field]) => [
      name,
      {
        type: toBetterAuthFieldType(field.type),
        required: field.nullable !== true,
        ...(field.default !== undefined ? { defaultValue: field.default } : {}),
        ...(field.columnName ? { fieldName: field.columnName } : {}),
        input: field.input === true,
      },
    ]),
  );
}

function applyBetterAuthOverrides(defaults: Record<string, any>, authConfig?: GencowAuthConfig) {
  const betterAuth = (authConfig as any)?.betterAuth;
  if (typeof betterAuth === "function") return betterAuth(defaults);
  return defaults;
}

function toAuthUserLike(user: any): AuthUserLike {
  return {
    id: user.id,
    email: user.email,
    name: user.name || user.email.split("@")[0],
    emailVerified: user.emailVerified,
    createdAt: user.createdAt,
    updatedAt: user.updatedAt,
  };
}

export function getAuthUserAdditionalFieldNames(authConfig?: GencowAuthConfig): string[] {
  return Object.keys((authConfig as any)?.user?.additionalFields ?? {});
}

export function buildGencowBetterAuthOptions(params: {
  authConfig?: GencowAuthConfig;
  database?: unknown;
  secret?: string;
  baseURL?: string;
  trustedOrigins?: string[];
  socialProviders?: Record<string, unknown>;
  plugins?: unknown[];
  port?: string;
  authHookContext?: (request?: unknown) => AuthHookContext;
  runAuthEventHook?: (label: string, fn: () => Promise<void>) => Promise<void>;
}) {
  const authConfig = params.authConfig;
  const userAdditionalFields = buildUserAdditionalFields(authConfig);
  const buildHookContext = params.authHookContext ?? ((request?: unknown) => ({ request }));
  const runHook =
    params.runAuthEventHook ??
    (async (_label: string, fn: () => Promise<void>) => {
      await fn();
    });
  const defaults: Record<string, any> = {
    ...(params.secret ? { secret: params.secret } : {}),
    ...(params.database ? { database: params.database } : {}),
    baseURL: params.baseURL || `http://localhost:${params.port || "5456"}`,
    basePath: "/api/auth",
    ...(userAdditionalFields ? { user: { additionalFields: userAdditionalFields } } : {}),
    emailAndPassword: {
      enabled: true,
      ...(authConfig?.emailVerification
        ? {
            requireEmailVerification: authConfig.emailVerification.requireEmailVerification ?? true,
            autoSignIn: false,
          }
        : {}),
      ...(authConfig?.passwordReset
        ? {
            resetPasswordTokenExpiresIn: authConfig.passwordReset.resetPasswordTokenExpiresIn,
            sendResetPassword: async ({ user, url, token }: any, request: unknown) => {
              try {
                await authConfig.passwordReset!.sendResetPassword(
                  {
                    user: toAuthUserLike(user),
                    url,
                    token,
                  },
                  buildHookContext(request),
                );
                console.log(`[auth] Reset password email sent to ${user.email}`);
              } catch (e: unknown) {
                const error = e instanceof Error ? e : new Error(String(e));
                const msg = error.message;
                console.error(`[auth] Failed to send reset password email to ${user.email}:`, msg);
                throw error;
              }
            },
            onPasswordReset: authConfig.passwordReset.onPasswordReset
              ? async ({ user }: any, request: unknown) => {
                  await runHook("onPasswordReset", () =>
                    authConfig.passwordReset!.onPasswordReset!(
                      { user: toAuthUserLike(user) },
                      buildHookContext(request),
                    ),
                  );
                }
              : undefined,
          }
        : {}),
    },
    ...(authConfig?.emailVerification
      ? {
          emailVerification: {
            sendOnSignUp: authConfig.emailVerification.sendOnSignUp ?? true,
            autoSignInAfterVerification: authConfig.emailVerification.autoSignInAfterVerification ?? true,
            sendVerificationEmail: async ({ user, url, token }: any, request: unknown) => {
              try {
                await authConfig.emailVerification!.sendVerificationEmail(
                  {
                    user: toAuthUserLike(user),
                    url,
                    token,
                  },
                  buildHookContext(request),
                );
                console.log(`[auth] Verification email sent to ${user.email}`);
              } catch (e: unknown) {
                const error = e instanceof Error ? e : new Error(String(e));
                const msg = error.message;
                console.error(`[auth] Failed to send verification email to ${user.email}:`, msg);
                throw error;
              }
            },
            beforeEmailVerification: authConfig.emailVerification.beforeEmailVerification
              ? async (user: any, request: unknown) => {
                  await runHook("beforeEmailVerification", () =>
                    authConfig.emailVerification!.beforeEmailVerification!(
                      toAuthUserLike(user),
                      buildHookContext(request),
                    ),
                  );
                }
              : undefined,
            afterEmailVerification: authConfig.emailVerification.afterEmailVerification
              ? async (user: any, request: unknown) => {
                  await runHook("afterEmailVerification", () =>
                    authConfig.emailVerification!.afterEmailVerification!(
                      toAuthUserLike(user),
                      buildHookContext(request),
                    ),
                  );
                }
              : undefined,
          },
        }
      : {}),
    session: {
      expiresIn: 60 * 60 * 24 * 7,
      updateAge: 60 * 60 * 24,
    },
    account: {
      encryptOAuthTokens: true,
    },
    socialProviders: params.socialProviders,
    plugins: params.plugins ?? [],
    trustedOrigins: params.trustedOrigins,
  };

  return applyBetterAuthOverrides(defaults, authConfig);
}
